// Faulty-word safeguard (pure): flags a word (TAP / STROKE / MASSAGE) the hardware reads in a way no human plausibly would.
export const WORDS = ['TAP', 'STROKE', 'MASSAGE'];
export const GUARD_CONFIG = {
  quietWindowMs: 20000, // (a) fires while nobody should be inputting (demo, opening / holding, hands-off step; NOT rules / instruct)
  quietMax: 6, // eager humans touch during the demo: it takes this many in 20 s…
  quietRegularMin: 3, // …or this many quiet reads among the last quietRegularN that are machine-regular (onset-timed)
  quietRegularN: 5, // 5 reads = 4 intervals
  tryWindowMs: 8000, // (b) during TRY, the same word read this often
  tryMax: 8,
  regularN: 6, // (c) machine-like regularity: N events whose intervals jitter less than this
  regularJitter: 0.1,
  regularMaxIntervalMs: 4000,
  stuckMs: 25000, // (d) touch held continuously → MASSAGE first, then STROKE if it recurs (→ fallback)
  // the detector itself caps a touch at 20 s (re-baseline), so (d) also fires on that cap
  touchCapMax: 2, // an affectionate 20 s hold in TRY is fine once; twice in 2 min (or one starting while nobody should touch) is not
  touchCapWindowMs: 120000,
  solvePhantomWindowMs: 20000, // suspicious solve: the solving word had quiet-time reads this recently
  solveRegularMinReads: 4, // …or ≥ 4 solving reads with machine-regular intervals
};

const fresh = () => ({ enabled: true, reason: null, at: null });

export function createWordGuard({ config = GUARD_CONFIG, onChange = () => {} } = {}) {
  const C = config;
  let ev = { TAP: [], STROKE: [], MASSAGE: [] };
  let status = { TAP: fresh(), STROKE: fresh(), MASSAGE: fresh() };
  // a stuck touch costs the longest word first; a second one costs STROKE too
  const stuckWord = () => (status.MASSAGE.enabled ? 'MASSAGE' : 'STROKE');
  let touchCaps = [];
  const keepMs = Math.max(C.quietWindowMs, C.tryWindowMs, 60000);

  function disable(word, reason, t = Date.now(), manual = false) {
    if (!status[word] || !status[word].enabled) return false;
    status[word] = { enabled: false, reason, at: t, manual };
    onChange(word, reason, false);
    return true;
  }
  function enable(word) {
    if (!status[word] || status[word].enabled) return;
    status[word] = fresh();
    ev[word] = [];
    onChange(word, null, true);
  }

  return {
    // one detected word; ctx: { t (ms), quiet (nobody should touch), live (TRY input) }
    observe(word, { t, quiet = false, live = false }) {
      if (!status[word] || !status[word].enabled) return null;
      const L = ev[word];
      L.push({ t, quiet, live });
      while (L.length && t - L[0].t > keepMs) L.shift();
      const q = L.filter((e) => e.quiet && t - e.t <= C.quietWindowMs).length;
      if (q >= C.quietMax) return disable(word, `phantom ${word} ×${q} while nobody should touch`, t) && word;
      const lastQ = L.slice(-C.quietRegularN);
      if (q >= C.quietRegularMin && lastQ.length === C.quietRegularN && lastQ.filter((e) => e.quiet).length >= C.quietRegularMin) {
        const iv = lastQ.slice(1).map((e, i) => e.t - lastQ[i].t);
        const mean = iv.reduce((a, b) => a + b, 0) / iv.length;
        const sd = Math.sqrt(iv.reduce((a, b) => a + (b - mean) ** 2, 0) / iv.length);
        if (mean > 0 && mean <= C.regularMaxIntervalMs && sd / mean < C.regularJitter) return disable(word, `phantom ${word} ×${q} while nobody should touch, machine-regular every ${Math.round(mean)} ms (±${Math.round((sd / mean) * 100)} %)`, t) && word;
      }
      const tr = L.filter((e) => e.live && t - e.t <= C.tryWindowMs).length;
      if (tr >= C.tryMax) return disable(word, `${word} read ×${tr} in ${C.tryWindowMs / 1000} s`, t) && word;
      const last = L.slice(-C.regularN);
      if (last.length === C.regularN) {
        const iv = last.slice(1).map((e, i) => e.t - last[i].t);
        const mean = iv.reduce((a, b) => a + b, 0) / iv.length;
        const sd = Math.sqrt(iv.reduce((a, b) => a + (b - mean) ** 2, 0) / iv.length);
        if (mean > 0 && mean <= C.regularMaxIntervalMs && sd / mean < C.regularJitter) return disable(word, `machine-regular ${word} every ${Math.round(mean)} ms (±${Math.round((sd / mean) * 100)} %)`, t) && word;
      }
      return null;
    },
    stuck(kind, ms, t) {
      if (ms <= C.stuckMs) return null;
      if (kind !== 'touch') return null;
      const word = stuckWord();
      return disable(word, `touch stuck on for ${Math.round(ms / 1000)} s`, t) ? word : null;
    },
    // detector cap: a touch held to the 20 s re-baseline (the hover band is not an input any more)
    capHit(kind, t, { quiet = false } = {}) {
      if (kind !== 'touch') return null;
      touchCaps = touchCaps.filter((x) => t - x <= C.touchCapWindowMs).concat(t);
      if (!quiet && touchCaps.length < C.touchCapMax) return null;
      const why = quiet ? 'touch stuck: held to the detector\'s 20 s cap, started while nobody should touch' : `touch stuck: held to the 20 s cap ×${touchCaps.length} in ${C.touchCapWindowMs / 1000} s`;
      const word = stuckWord();
      return disable(word, why, t) ? word : null;
    },
    // a phrase just completed: veto it when the solving reads look like the hardware, not a person.
    // reads = [{ word, t }] (t on the same clock as observe). Returns the word to disable, or null.
    vetSolve(reads, t) {
      const words = reads.map((r) => r.word).filter((w) => status[w] && status[w].enabled);
      if (!words.length) return null;
      const only = words.every((w) => w === words[0]) ? words[0] : null;
      if (only && ev[only].some((e) => e.quiet && t - e.t <= C.solvePhantomWindowMs)) return disable(only, `suspicious solve: phrase completed only by ${only}, which was firing while nobody should touch`, t) && only;
      if (reads.length >= C.solveRegularMinReads) {
        const iv = reads.slice(1).map((r, i) => r.t - reads[i].t);
        const mean = iv.reduce((a, b) => a + b, 0) / iv.length;
        const sd = Math.sqrt(iv.reduce((a, b) => a + (b - mean) ** 2, 0) / iv.length);
        if (mean > 0 && sd / mean < C.regularJitter) {
          const count = {};
          words.forEach((w) => (count[w] = (count[w] || 0) + 1));
          const w = Object.keys(count).sort((a, b) => count[b] - count[a])[0];
          return disable(w, `suspicious solve: machine-regular reads every ${Math.round(mean)} ms (±${Math.round((sd / mean) * 100)} %)`, t) && w;
        }
      }
      return null;
    },
    disable: (word, reason = 'host', t) => disable(word, reason, t, true),
    enable,
    toggle(word, t) {
      if (status[word].enabled) disable(word, 'disabled by host', t, true);
      else enable(word);
    },
    enableAll: () => WORDS.forEach(enable),
    disabled: () => new Set(WORDS.filter((w) => !status[w].enabled)),
    getStatus: () => ({ tap: { ...status.TAP }, stroke: { ...status.STROKE }, massage: { ...status.MASSAGE } }),
    reset() {
      touchCaps = [];
      ev = { TAP: [], STROKE: [], MASSAGE: [] };
      status = { TAP: fresh(), STROKE: fresh(), MASSAGE: fresh() };
    },
  };
}

// Remaining phrases without the disabled words; phrases shorter than 2 steps are padded with working words,
// distinct from their neighbours where possible. Levels before `fromIdx` keep their original phrases.
export function adaptSequences(levels, disabled, fromIdx = 0) {
  const out = levels.map((l) => [...l.sequence]);
  const working = WORDS.filter((w) => !disabled.has(w));
  if (!disabled.size || !working.length) return out;
  const strip = (i) => levels[i].sequence.filter((x) => !disabled.has(x));
  for (let i = fromIdx; i < levels.length; i += 1) {
    const base = strip(i);
    if (base.length >= 2) {
      out[i] = base;
      continue;
    }
    const prev = i > 0 ? out[i - 1].join() : '';
    const next = i + 1 < levels.length ? strip(i + 1).join() : '';
    let pick = null;
    for (let k = 0; k < working.length && !pick; k += 1) {
      const cand = [...base];
      for (let j = k; cand.length < 2; j += 1) cand.unshift(working[(j + i) % working.length]);
      if (cand.join() !== prev && cand.join() !== next) pick = cand;
    }
    if (!pick) {
      pick = [...base];
      while (pick.length < 2) pick.unshift(working[i % working.length]);
    }
    out[i] = pick;
  }
  return out;
}
